import { Link } from '@backstage/core-components';
import CardHeader from '@mui/material/CardHeader';
import { FavoriteEntity } from '@backstage/plugin-catalog-react';
import { Tooltip } from '@material-ui/core';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import Stack from '@mui/material/Stack';
import Avatar from '@mui/material/Avatar';
import { useCard } from '../../lib/context';
import { CardType } from '../../lib/types';

// only show favorite icon when not actionable (clickable card)
const CardFavoriteIcon = () => {
  const { entity, actionable } = useCard();

  return entity && !actionable ? (
    <FavoriteEntity
      size="small"
      entity={entity}
      style={{ margin: '-2px', padding: '2px' }}
    />
  ) : null;
};

// if entity, icon, and catalog route are present and card is not actionable (clickable), wrap icon in link
const CardIcon = () => {
  const { entity, title, catalogRoute, Icon, actionable } = useCard();

  if (!entity || !Icon || !catalogRoute || actionable) return Icon && <Icon />;

  return (
    <Tooltip
      title={`Show details of ${title}`}
      placement="top"
      PopperProps={{
        modifiers: {
          offset: { offset: '0, -12' },
        },
      }}
    >
      <Link to={catalogRoute} color="inherit">
        <Icon />
      </Link>
    </Tooltip>
  );
};

const CardTitleIcons = () => {
  return (
    <Stack direction="column" alignItems="center" gap={0.5} padding={0.5}>
      <CardFavoriteIcon />
      <CardIcon />
    </Stack>
  );
};

const CardSubheader = () => {
  const { type, entity, link, created } = useCard();

  // determine and format subheader text
  let cardSubheader = type;

  if (entity && entity.kind.toLowerCase() !== 'template') {
    const specType =
      typeof entity.spec?.type === 'string' ? entity.spec.type : undefined;

    cardSubheader = specType ? `${entity.kind} - ${specType}` : entity.kind;
  }
  // for announcement cards, show created date instead of type
  if (type === CardType.announcement) {
    cardSubheader = created;
  }

  // for link cards, show description
  if (type === CardType.link) {
    cardSubheader = link?.description;
  }

  return <Typography variant="subtitle2">{cardSubheader}</Typography>;
};

export const CardTitle = () => {
  const theme = useTheme();
  const {
    type,
    title,
    link,
    maxTitleLength,
    themedStyles,
    externalStyles,
    withoutTheme,
    avatar,
  } = useCard();

  const isDarkTheme = theme.palette.mode === 'dark';

  const slotProps = {
    avatar: {
      style: { ...externalStyles?.avatar },
    },
    title: {
      style: {
        fontSize: '1.125rem',
        fontWeight: 600,
        ...externalStyles?.title,
      },
    },
    subheader: {
      style: { opacity: 0.5, color: 'inherit', ...externalStyles?.subheader },
    },
    action: {
      style: {
        margin: `0 0 0 ${theme.spacing(1)}px`,
        ...externalStyles?.icon,
      },
    },
  };

  const headerClasses =
    type === CardType.announcement || withoutTheme
      ? undefined
      : { root: themedStyles?.header };

  const cardTitle =
    title && maxTitleLength && title.length > maxTitleLength
      ? `${title.substring(0, maxTitleLength)}...`
      : title;

  let cardAvatar = avatar ?? undefined;
  if (type === CardType.link && !avatar) {
    cardAvatar = (
      <Avatar
        variant="rounded"
        style={{
          backgroundColor: isDarkTheme ? '#666666' : '#e8e8e8',
          color: isDarkTheme ? 'initial' : 'inherit',
        }}
      >
        {link?.icon && <link.icon />}
      </Avatar>
    );
  }

  const cardAction = type === CardType.link ? undefined : <CardTitleIcons />;

  return (
    <CardHeader
      slotProps={slotProps}
      title={<span title={title}>{cardTitle}</span>}
      avatar={cardAvatar}
      subheader={<CardSubheader />}
      action={cardAction}
      classes={headerClasses}
    />
  );
};
